import { Component, EventEmitter, HostListener, Input, Output, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RagLogComponent } from './rag-log.component';
import type { RagAuditRequest } from './rag-log.component';
import { CallAuditComponent } from './call-audit.component';
import { SecurityInfoComponent } from './security-info.component';
import { TranscriptPlayerComponent } from './transcript-player.component';
import { SessionInsightsPanelComponent } from './session-insights-panel.component';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { TrackingApiService } from '../services/tracking-api.service';
import { SpeakerSides, audioSrcOf, isAiTurn, speakerSides } from '../transcript-timing.util';
import { TurnProof, buildTurnProofMap, proofSourceLabel } from '../transcript-proof.util';
import {
  durationSecToStr, formatDuration, parseApiDate, shortId, statusClass, statusDot, statusLabel,
} from '../tracking-format.util';
import type {
  RagAudit, CallAudit,
  CostRow, ReportAuditEntry, ReportEvent, ReportRecording, ReportSlide, ReportUserDetails,
  SessionCostSummary, SessionReport, TrackingParticipant, TrackingSession,
} from '../tracking-demo.models';

type ReportTab = 'transcript' | 'research' | 'slides' | 'rag' | 'audit' | 'timeline' | 'admin' | 'video' | 'insights' | 'costing';

/** Manifest page keys are stripped/lowercased (`01intro`) while older slide history recorded
 *  the pre-rename key (`01_intro`). Compare on this form so a renamed page still resolves to
 *  its asset instead of falling through to `livecall/products/{key}/slides/{idx}`, which 404s
 *  on anything that isn't a product slug. Normalised keys stay distinct across the manifest. */
const deckKeyId = (key: string): string => key.toLowerCase().replace(/[^a-z0-9]/g, '');

/** A timeline event pre-joined with its display timing (absolute, offset, gap-from-previous). */
interface TimelineRow {
  e: ReportEvent;
  time: string;
  offset: string;
  gap: string | null;
}

/** A transcript turn pre-joined with its display timing (24h clock + a date divider
 *  shown before the first turn and whenever the calendar day rolls over). */
interface TranscriptRow {
  turn: SessionReport['transcript'][number];
  time: string;
  dateLabel: string | null;
}

/** One label/value line of the left rail's "User details" tile (href → rendered as a link). */
interface UserDetailRow {
  label: string;
  value: string;
  href?: string;
}

/** One row of the Actions tab — a participant's deliberate UI action (from the
 *  client-event log) or a CRM admin's audit entry, merged into one chronological trail.
 *  'observer' = an admin acting as the covert in-room watcher (audit-sourced too). */
interface ActionRow {
  label: string;
  name: string;
  role: 'host' | 'guest' | 'admin' | 'observer';
  detail?: string;
  ip?: string;
  at?: string | null;
}

/**
 * Full-screen session report — everything that happened in one demo, read from the durable
 * stores: merged session + security, participant roster, persisted transcript + slides
 * (each deck slide re-rendered from the same public asset the prospect saw), research brief,
 * call outcome, the client-event timeline, and the admin-action trail.
 *
 * Sits at z-[1000] — ABOVE the shell navbar/sidebar (z-[999]) so its own header (with the
 * back button) is never buried under the CRM chrome. Esc also closes it.
 */
@Component({
  selector: 'app-session-report',
  standalone: true,
  imports: [CommonModule, RagLogComponent, SecurityInfoComponent, CallAuditComponent, TranscriptPlayerComponent, SessionInsightsPanelComponent, IconComponentV2],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './session-report.component.html',
})
export class SessionReportComponent {
  private readonly api = inject(TrackingApiService);
  private readonly sanitizer = inject(DomSanitizer);

  @Input({ required: true }) set sessionId(sid: string) {
    this._sid.set(sid);
    void this.load(sid);
  }
  @Output() closed = new EventEmitter<void>();

  readonly _sid = signal<string>('');
  readonly report = signal<SessionReport | null>(null);

  /** Debug dock → "Build waveform": compute the envelope server-side, then re-feed
   *  the report so the player's rows redraw with it. */
  async onBuildWaveform(): Promise<void> {
    const r = this.report();
    if (!r?.recording) return;
    try {
      const { envelope } = await this.api.buildEnvelope(r.session.sessionId);
      this.report.set({ ...r, recording: { ...r.recording, envelope } });
    } catch { /* the api service already surfaced the error */ }
  }
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly tab = signal<ReportTab>('transcript');
  readonly tabs: Array<{ key: ReportTab; label: string }> = [
    { key: 'transcript', label: 'Transcript' },
    { key: 'research', label: 'Research & Outcome' },
    { key: 'slides', label: 'Slides' },
    { key: 'rag', label: 'RAG lookups' },
    { key: 'audit', label: 'Audit' },
    { key: 'timeline', label: 'Timeline' },
    { key: 'admin', label: 'Actions' },
    { key: 'video', label: 'Video' },
    { key: 'insights', label: 'Insights' },
    { key: 'costing', label: 'Costing' },
  ];

  /** Per-demo API spend (live_call_api_costs) — loaded alongside the report, keyed to the
   *  report's PRIMARY sid (cost rows are written under the per-call sid, never the room shadow). */
  readonly costs = signal<SessionCostSummary | null>(null);
  readonly costsError = signal(false);

  /** Deck manifest (key → asset file), so each recorded deck slide can be re-rendered. */
  private deckFileByKey = new Map<string, string>();
  /** Product keyname the manifest (and asset URLs) were scoped to — null = default product. */
  private deckProductToken: string | null = null;
  /** Identity-stable iframe srcs (a new SafeResourceUrl per CD pass would reload frames). */
  private readonly srcCache = new Map<string, SafeResourceUrl>();
  readonly timeline = signal<TimelineRow[]>([]);
  /** Actions tab rows: participant actions + admin audit, merged & sorted per load. */
  readonly actionRows = signal<ActionRow[]>([]);
  /** Actions tab role filter (chips above the list). */
  readonly actionFilter = signal<'all' | ActionRow['role']>('all');
  readonly actionFilters: Array<{ key: 'all' | ActionRow['role']; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'host', label: 'Host' },
    { key: 'guest', label: 'Guest' },
    { key: 'admin', label: 'Admin' },
    { key: 'observer', label: 'Observer' },
  ];
  readonly filteredActionRows = computed(() => {
    const f = this.actionFilter();
    return f === 'all' ? this.actionRows() : this.actionRows().filter((a) => a.role === f);
  });
  /** Transcript turns with their 24h time + day dividers — precomputed per load. */
  readonly transcriptRows = signal<TranscriptRow[]>([]);
  /** "User details" tile rows (left rail) — precomputed per load, empty rows dropped. */
  readonly userRows = signal<UserDetailRow[]>([]);
  /** Carousel position in the Slides tab (0-based). */
  readonly slideIdx = signal(0);

  readonly durationSecToStr = durationSecToStr;
  readonly shortId = shortId;
  readonly statusClass = statusClass;
  readonly statusDot = statusDot;
  readonly statusLabel = statusLabel;

  /** Handed to the RAG tab so it can run a claim attribution without reaching for the API
   *  service itself (which would drag the whole core graph into that component). */
  readonly auditRagLookup = (body: RagAuditRequest): Promise<RagAudit> =>
    this.api.auditRagLookup(this._sid(), body);

  /** RAG-tab lookup to auto-expand when the Audit tab's proof link jumps there. */
  readonly ragFocus = signal<number | null>(null);

  focusRagLookup(i: number): void {
    this.ragFocus.set(i);
    this.tab.set('rag');
  }

  /** Handed to app-call-audit (the auditFn pattern — the child never sees the api).
   *  Also refreshes the transcript tab's per-turn proof map from the fresh audit,
   *  so re-running an old audit makes the proof icons appear without a reload. */
  readonly rerunCallAudit = async (): Promise<CallAudit> => {
    const audit = await this.api.runCallAudit(this._sid(), this.report()?.session?.callId);
    this.turnProofs.set(buildTurnProofMap(audit, this.report()?.transcript ?? []));
    return audit;
  };

  // ── transcript per-turn proof (the audit's turn↔lookup map, inline) ──
  readonly turnProofs = signal<Map<number, TurnProof>>(new Map());
  readonly proofOpen = signal<Set<number>>(new Set());
  readonly sourceLabel = proofSourceLabel;

  proofFor(i: number): TurnProof | undefined {
    return this.turnProofs().get(i);
  }

  toggleProof(i: number): void {
    this.proofOpen.update((s) => {
      const next = new Set(s);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }

  proofBtnClass(p: TurnProof): string {
    switch (p.state) {
      case 'failure': return 'border-rose-300 text-rose-700 dark:border-rose-800 dark:text-rose-400';
      case 'grounded': return 'border-emerald-300 text-emerald-700 dark:border-emerald-800 dark:text-emerald-400';
      default: return 'border-gray-300 text-slate-500 dark:border-line dark:text-ink-soft/70';
    }
  }

  proofDotClass(p: TurnProof): string {
    switch (p.state) {
      case 'failure': return 'bg-rose-500';
      case 'grounded': return 'bg-emerald-500';
      default: return 'bg-gray-400';
    }
  }

  claimClass(verdict: string): string {
    return verdict === 'supported' ? 'bg-emerald-100 text-emerald-700'
      : verdict === 'contradicted' ? 'bg-rose-100 text-rose-700'
        : 'bg-amber-100 text-amber-700';
  }

  @HostListener('document:keydown.escape')
  onEscape(): void { this.closed.emit(); }

  @HostListener('document:keydown.arrowleft')
  onArrowLeft(): void { if (this.tab() === 'slides') this.goSlide(this.slideIdx() - 1); }

  @HostListener('document:keydown.arrowright')
  onArrowRight(): void { if (this.tab() === 'slides') this.goSlide(this.slideIdx() + 1); }

  private async load(sid: string): Promise<void> {
    this.report.set(null);
    this.error.set(null);
    this.costs.set(null);
    this.costsError.set(false);
    this.loading.set(true);
    try {
      // The deck manifest resolves deckKey → asset file for the slide previews (best-effort).
      const reportPromise = this.api.getReport(sid);
      const [r, decks] = await Promise.all([reportPromise, this.loadDecks(reportPromise)]);
      if (this._sid() !== sid) return; // superseded by a newer open
      this.deckFileByKey = new Map(decks.map((d) => [deckKeyId(d.key), d.file]));
      this.report.set(r);
      this.timeline.set(this.buildTimeline(r));
      this.actionRows.set(this.buildActionRows(r));
      this.actionFilter.set('all');
      this.transcriptRows.set(this.buildTranscriptRows(r));
      this.turnProofs.set(buildTurnProofMap(r.callAudit, r.transcript ?? []));
      this.proofOpen.set(new Set());
      this.userRows.set(this.buildUserRows(r));
      this.slideIdx.set(0);
      this.sides = speakerSides(r);
      this.tab.set(r.transcript?.length ? 'transcript' : (r.research || r.outcome) ? 'research' : 'timeline');
      void this.loadCosts(r.session?.sessionId || sid);
    } catch {
      this.error.set('Could not load this session’s report.');
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * The deck manifest for THIS session's product. The manifest (and the asset route
   * behind it) is product-scoped on the backend — fetched bare it answers with the
   * DEFAULT product's catalog, so a non-default product's page keys missed the map
   * and its deck slides fell through to the product-slides URL, which 404s on a
   * page key. Nothing here names a product: any newly registered product resolves
   * the same way.
   *
   * Fast path (panel opened under one product): the route's product key is the
   * token — fetch in parallel with the report. Combined view: wait for the report's
   * session row and map its productId → keyname via the products list. Best-effort
   * like before: any miss degrades to the default catalog, never to a failed report.
   */
  private async loadDecks(
    reportPromise: Promise<SessionReport>,
  ): Promise<Array<{ key: string; file: string; title?: string }>> {
    let token = (this.api.productKey() || '').trim();
    if (!token || token === 'all') {
      token = '';
      try {
        const pid = (await reportPromise).session?.productId;
        if (pid != null) {
          const { items } = await this.api.getProducts();
          token = items.find((p) => p.productId === pid)?.keyname || '';
        }
      } catch {
        // Report failure surfaces via load()'s own await; a products miss just
        // means the default catalog — the pre-fix behaviour.
      }
    }
    this.deckProductToken = token || null;
    return this.api.getDecks(this.deckProductToken);
  }

  /** Best-effort: the Costing tab shows its own empty/error state, never blocks the report. */
  private async loadCosts(sid: string): Promise<void> {
    try {
      const c = await this.api.getSessionCosts(sid);
      if (this.report()?.session?.sessionId !== sid && this._sid() !== sid) return; // superseded
      this.costs.set(c);
    } catch {
      this.costsError.set(true);
    }
  }

  session(): TrackingSession | null { return this.report()?.session ?? null; }

  // ── header helpers ──
  dateRange(): string {
    const s = this.session();
    const start = parseApiDate(s?.startedAt);
    if (!s || !start) return '—';
    const end = parseApiDate(s.endedAt);
    const day = start.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    const t = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return end ? `${day} · ${t(start)} → ${t(end)}` : `${day} · ${t(start)}`;
  }

  outcomeLabel(): string {
    const s = this.session();
    if (!s) return '—';
    const map: Record<string, string> = {
      summary: 'Completed · summary saved',
      host_ended: 'Host ended the demo',
      admin_disconnect: 'Terminated by admin',
      sweeper_idle: 'Idle timeout',
      expired: 'Expired',
    };
    const base = map[s.endReason || ''] || (s.endReason || '—');
    return s.adminDisconnectReason ? `${base} (${s.adminDisconnectReason.replace(/_/g, ' ')})` : base;
  }

  stat(kind: 'participants' | 'turns' | 'slides' | 'tokens' | 'admin'): number {
    const r = this.report();
    if (!r) return 0;
    switch (kind) {
      case 'participants': return r.participants.length;
      case 'turns': return r.transcript.length;
      case 'slides': return r.slides.length;
      case 'tokens': return r.session.tokenCount || 0;
      case 'admin': return this.actionRows().length;
    }
  }

  // ── timing ──
  /** Local wall-clock HH:MM:SS (backend naive-UTC strings are pinned to UTC first). */
  timeOf(iso?: string | null): string {
    const d = parseApiDate(iso);
    return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
  }

  /** "+m:ss" elapsed since the session started ('' when either side is unknown). */
  offsetOf(iso?: string | null): string {
    const at = parseApiDate(iso)?.getTime();
    const start = parseApiDate(this.session()?.startedAt)?.getTime();
    if (at == null || start == null) return '';
    return `+${formatDuration(Math.max(0, at - start))}`;
  }

  /** Pre-computed once per load (runs before the report signal settles, so `r` is explicit). */
  private buildTimeline(r: SessionReport): TimelineRow[] {
    const start = parseApiDate(r.session.startedAt)?.getTime() ?? null;
    let prev: number | null = null;
    return (r.events || []).map((e) => {
      const d = parseApiDate(e.at);
      const at = d?.getTime() ?? null;
      const row: TimelineRow = {
        e,
        time: d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—',
        offset: at != null && start != null ? `+${formatDuration(Math.max(0, at - start))}` : '',
        gap: at != null && prev != null ? `+${formatDuration(Math.max(0, at - prev))}` : null,
      };
      if (at != null) prev = at;
      return row;
    });
  }

  /** "User details" tile rows — the report's userDetails block, falling back to the same
   *  fields scattered on the session/outcome/research for reports from an older backend. */
  private buildUserRows(r: SessionReport): UserDetailRow[] {
    const u: ReportUserDetails = r.userDetails || {};
    const name = u.name || r.session.hostName;
    const email = u.email;
    const company = u.company || r.session.companyName
      || r.outcome?.businessName || r.research?.businessName;
    const website = u.website || r.outcome?.websiteUrl || r.research?.website;
    const industry = u.industry || r.research?.brief?.industry;
    const rows: UserDetailRow[] = [];
    if (name) rows.push({ label: 'Name', value: name });
    if (email) rows.push({ label: 'Email', value: email, href: `mailto:${email}` });
    // Only present when the demo was joined by phone (stamped at the dial-in PIN gate).
    // Older sessions carry the event's masked hint ('***42') — display it, but only
    // tel:-link a real number.
    if (u.phone) {
      rows.push({
        label: 'Phone', value: u.phone,
        href: /^[+\d]/.test(u.phone) ? `tel:${u.phone}` : undefined,
      });
    }
    if (website) {
      rows.push({
        label: 'Website', value: website.replace(/^https?:\/\//, ''),
        href: /^https?:\/\//i.test(website) ? website : `https://${website}`,
      });
    }
    if (u.role) {
      rows.push({
        label: 'Service',
        value: u.role === 'customer' ? 'I want services' : 'I offer services',
      });
    }
    // The setup form's "Name" is stored as the business name too — only show Company
    // when it actually says something the Name row doesn't.
    if (company && company !== name) rows.push({ label: 'Company', value: company });
    rows.push({ label: 'Industry', value: industry || '—' });
    if (u.region) rows.push({ label: 'Region', value: u.region });
    if (u.language) rows.push({ label: 'Language', value: u.language });
    return rows;
  }

  // ── transcript ──
  /** Turns + display timing: 24h HH:MM per turn ('' when the turn has no ts — older
   *  sessions), and a divider label before the first stamped turn / on each day change
   *  ("Today" / "Yesterday" / the date). */
  private buildTranscriptRows(r: SessionReport): TranscriptRow[] {
    let prevDay: string | null = null;
    return (r.transcript || []).map((turn) => {
      const d = parseApiDate(turn.ts);
      const day = d ? d.toDateString() : null;
      const row: TranscriptRow = {
        turn,
        time: d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }) : '',
        dateLabel: day && day !== prevDay ? this.dayLabel(d!) : null,
      };
      if (day) prevDay = day;
      return row;
    });
  }

  private dayLabel(d: Date): string {
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }

  /** Speaker names per side for this session (aiAgentName, hostName, participants) —
   *  feeds the structural AI/human rule in transcript-timing.util, which is what keeps
   *  an agent that isn't literally "Alex" (e.g. AVA) off the prospect side. Rebuilt per
   *  report load; the default covers the first render before a report lands. */
  private sides: SpeakerSides = { ai: new Set(['alex', 'ai', 'agent']), human: new Set(['prospect']) };

  isProspect(turn: SessionReport['transcript'][number]): boolean {
    return !isAiTurn(turn, this.sides);
  }

  turnSpeaker(turn: SessionReport['transcript'][number]): string {
    if (this.isProspect(turn)) {
      return turn.speaker || turn.who || this.session()?.hostName || this.session()?.companyName || 'Prospect';
    }
    return turn.who || 'AI';
  }

  // ── participants ──
  roleBadge(role: string): string {
    return role === 'host' ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-300'
      : role === 'ai' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'
        : 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300';
  }

  participantWindow(p: TrackingParticipant): string {
    const t = (iso: string | null) => {
      const d = parseApiDate(iso);
      return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : null;
    };
    const joined = t(p.joinedAt);
    const left = t(p.leftAt);
    if (joined && left) return `${joined} → ${left}`;
    if (joined) return `joined ${joined}`;
    return '—';
  }

  /** How long the participant was actually in the call ('' when the window is open/unknown). */
  participantDuration(p: TrackingParticipant): string {
    const a = parseApiDate(p.joinedAt)?.getTime();
    const b = parseApiDate(p.leftAt)?.getTime();
    if (a == null || b == null || b < a) return '';
    return formatDuration(b - a);
  }

  /** One "what they joined from" line per participant: their own client-info
   *  capture (host at call start, guest at the approval gate) compressed to
   *  browser · OS · device. A phone leg has no browser to capture, so its line
   *  says so honestly; the AI gets no line at all. */
  participantDevice(p: TrackingParticipant): string {
    if (p.role === 'ai') return '';
    const sec = p.security;
    if (!sec) return p.via === 'phone' ? 'Phone (carrier call — no browser)' : '';
    const parts = [
      [sec.browser, sec.browserVersion ? String(sec.browserVersion).split('.')[0] : '']
        .filter(Boolean).join(' '),
      sec.os,
      sec.deviceType && sec.deviceType !== 'unknown' ? sec.deviceType : '',
      // A native-app launch (the link's declared `device_type`) is part of
      // "what they joined from" — a web link adds nothing here.
      sec.appPlatform ? `via ${sec.appPlatform === 'ios' ? 'iOS' : sec.appPlatform === 'android' ? 'Android' : sec.appPlatform} app` : '',
    ].filter(Boolean);
    return parts.join(' · ');
  }

  // ── slides ──
  /** The slide the carousel is showing (bounds-safe). */
  currentSlide(): ReportSlide | null {
    const slides = this.report()?.slides || [];
    return slides[Math.min(this.slideIdx(), slides.length - 1)] ?? null;
  }

  /** Jump the carousel (clamped — arrows and pills share this). */
  goSlide(i: number): void {
    const count = this.report()?.slides.length || 0;
    if (!count) return;
    this.slideIdx.set(Math.max(0, Math.min(count - 1, i)));
  }

  /**
   * Raw URL of the exact page the prospect saw, or null when it can't be reconstructed.
   *
   * `deckKey` is overloaded across the two agents that write slide history, and only the
   * deck manifest can tell them apart: the LEGACY agent (live_call_agent) records a deck
   * PAGE key (`01_intro`, `overview`) while the REVAMP agent (livecall_agent, see
   * slide_history.py `SlideHistory(deck_key=slug)`) records a PRODUCT SLUG (`credee`).
   * Both carry a `slideIndex`, so it is not a discriminator — checking the manifest first
   * is what keeps a legacy page key out of `livecall/products/{slug}/slides/{idx}`, which
   * 404s on it. Page keys and product slugs do not overlap.
   *
   * Priority:
   *   1. explicit `assetFile` (events that carried the served filename),
   *   2. `deckKey` in the legacy deck manifest → its static asset file,
   *   3. otherwise `deckKey` is a product slug → the live product-slides endpoint,
   *      the SAME URL the demo iframed.
   * Anything with none of these (a truly ephemeral AI visual) gets a placeholder.
   */
  private slideAssetUrl(sl: ReportSlide): string | null {
    if (sl.assetFile) return this.api.assetUrl(sl.assetFile, this.deckProductToken);
    const file = sl.deckKey ? this.deckFileByKey.get(deckKeyId(sl.deckKey)) : null;
    if (file) return this.api.assetUrl(file, this.deckProductToken);
    if (sl.deckKey && sl.slideIndex != null) return this.api.deckSlideUrl(sl.deckKey, sl.slideIndex);
    return null;
  }

  /** Identity-stable iframe src for the current slide (cached per URL — a fresh
   *  SafeResourceUrl each CD pass would reload the frame). */
  slideSrc(sl: ReportSlide): SafeResourceUrl | null {
    const url = this.slideAssetUrl(sl);
    if (!url) return null;
    let src = this.srcCache.get(url);
    if (!src) {
      src = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.srcCache.set(url, src);
    }
    return src;
  }

  /** Raw slide URL for the "Open" affordance on preview-able slides. */
  slideHref(sl: ReportSlide): string | null {
    return this.slideAssetUrl(sl);
  }

  // 'financingFlow' is a RETIRED stage (2026-09-11 — the walkthrough became a deck
  // group). It is kept in both maps on purpose: these render sessions recorded BEFORE
  // the change, whose slide rows still carry that stage. Both take a bare string, not
  // the live StageType union, so they do not track its removal. Do not "clean" these
  // up — dropping them would render old reports as unlabelled grey chips.
  slideChip(stage?: string | null): string {
    switch (stage) {
      case 'deck': return 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300';
      case 'financingFlow': return 'bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-300';
      case 'roi': return 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300';
      case 'caseStudy': return 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300';
      case 'portal': return 'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300';
      case 'scenario': return 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300';
      default: return 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300';
    }
  }

  stageLabel(stage?: string | null): string {
    const map: Record<string, string> = {
      deck: 'deck', slide: 'AI slide', roi: 'ROI', caseStudy: 'case study',
      portal: 'portal', scenario: 'scenario', financingFlow: 'financing flow',
    };
    return map[stage || ''] || stage || 'slide';
  }

  // ── timeline ──
  /** "gemini.turn_complete" → "Gemini turn complete" (the raw type stays visible as mono). */
  eventLabel(type: string): string {
    const words = type.replace(/[._-]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  /** One-line human summary of a timeline event's payload (kept terse on purpose). */
  eventDetail(e: ReportEvent): string {
    const p = e.payload;
    if (!p || typeof p !== 'object') return '';
    const bits: string[] = [];
    for (const key of ['reason', 'code', 'tool', 'stage', 'topic', 'deck', 'status', 'cached', 'step', 'name']) {
      if (p[key] !== undefined && p[key] !== null && `${p[key]}` !== '') bits.push(`${key}: ${p[key]}`);
    }
    return bits.slice(0, 4).join(' · ');
  }

  eventDotClass(type: string): string {
    // Before the error rule: `recording.stop` must read as a REC marker, not a failure.
    if (/^recording\./i.test(type)) return 'bg-rose-500';
    if (/error|fail|closed|goaway|disconnect/i.test(type)) return 'bg-red-400';
    if (/call\.start|connected|open/i.test(type)) return 'bg-emerald-400';
    if (/call\.end/i.test(type)) return 'bg-amber-400';
    if (/slide|deck|knowledge/i.test(type)) return 'bg-indigo-400';
    return 'bg-gray-400';
  }

  // ── video ──
  recording(): ReportRecording | null { return this.report()?.recording ?? null; }

  /** Audio for the transcript tab — when present, the tab renders the synced player. */
  audioSrc(): string | null { return audioSrcOf(this.recording()); }

  /** The rendered replay mp4 (live_call_recordings.video_render_url) — the Video tab's source. */
  videoSrc(): string | null { return this.recording()?.videoRenderUrl || null; }

  /** What the Video tab should show when there is no playable mp4 (status-aware empty state). */
  videoState(): 'ready' | 'rendering' | 'failed' | 'none' {
    const rec = this.recording();
    if (rec?.videoRenderUrl) return 'ready';
    if (rec?.renderStatus === 'running') return 'rendering';
    if (rec?.renderStatus === 'failed') return 'failed';
    return 'none';
  }

  /** Recording length as m:ss ('' when unknown). */
  videoDuration(): string {
    const ms = this.recording()?.durationMs;
    return ms ? formatDuration(ms) : '';
  }

  /** Raw live_call_recordings.render_status, shown as a chip beside the Video tab label
   *  (null → no chip: the session has no recording row at all). */
  renderStatus(): string | null {
    const rec = this.recording();
    if (!rec) return null;
    return rec.renderStatus || (rec.videoRenderUrl ? 'complete' : null);
  }

  renderStatusClass(): string {
    switch (this.videoState()) {
      case 'ready': return 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300';
      case 'rendering': return 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300 animate-pulse';
      case 'failed': return 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400';
    }
  }

  // ── costing ──
  /** "$0.0123" — adaptive decimals so micro-costs never round away to $0.0000. */
  usd(n: number | null | undefined): string {
    const v = Number(n ?? 0);
    if (!v) return '$0';
    return `$${v.toFixed(v >= 0.01 ? 4 : 6)}`;
  }

  tokens(n: number | null | undefined): string {
    return n == null ? '—' : n.toLocaleString();
  }

  serviceLabel(service: string): string {
    const map: Record<string, string> = {
      gemini_live: 'Gemini Live (voice)',
      gemini_aux: 'Gemini (server calls)',
    };
    return map[service] || service;
  }

  opLabel(op?: string | null): string {
    return (op || 'unknown').replace(/_/g, ' ');
  }

  /** Total input/output tokens across services (summary strip). */
  tokenTotal(kind: 'in' | 'out'): number {
    const svcs = this.costs()?.byService || [];
    return svcs.reduce((sum, s) => sum + (kind === 'in' ? s.inputTokens : s.outputTokens), 0);
  }

  /** Per-row secondary usage ("1,234 thinking · 567 cached · 1 search"). */
  rowExtras(row: CostRow): string {
    const bits: string[] = [];
    if (row.thinking_tokens) bits.push(`${row.thinking_tokens.toLocaleString()} thinking`);
    if (row.cached_tokens) bits.push(`${row.cached_tokens.toLocaleString()} cached`);
    if (row.search_requests) bits.push(`${row.search_requests} search`);
    return bits.join(' · ');
  }

  // ── actions ──
  /** Client-event types that count as deliberate user actions. Anything not listed
   *  here stays Timeline-only (lifecycle noise like connection.* / gemini.*). */
  private static readonly ACTION_EVENT_TYPES = new Set([
    'call.start', 'call.hold', 'call.resume', 'call.end',
    'action.copy_link', 'action.connect_agent', 'action.signup',
    'action.mic', 'action.camera', 'action.ai_text',
    // Phone dial-in rows are written SERVER-side (routes/dialin.py + sip.py):
    // the demo page can't report them — it's suspended behind the caller's
    // own dialer in exactly the sessions that have a phone leg.
    'action.phone_dialin', 'action.phone_joined', 'action.phone_left',
    'action.phone_bridge_failed',
  ]);

  /** Audit actions performed by an admin acting AS the covert in-room observer —
   *  tagged 'observer' so the role filter can single them out. Moderation acts
   *  (mute/remove) stay 'admin': they can come from the dashboard or the observer
   *  bar and the row can't tell which. */
  private static readonly OBSERVER_AUDIT_ACTIONS = new Set([
    'joined_as_observer', 'watched_session', 'joined_from_observer',
  ]);

  /** Audit whitelist: only these render in the Actions tab, with these labels.
   *  Anything else in the audit trail (some backends mirror raw client events —
   *  media.mic, tool.call — into it) is machine noise, not a person's action,
   *  and is dropped rather than shown as a baffling "Admin" row. */
  private static readonly AUDIT_ACTION_LABELS: Record<string, string> = {
    viewed_session: 'Viewed session',
    watched_session: 'Watched session live',
    joined_as_observer: 'Joined as observer',
    joined_from_observer: 'Joined the meeting from observer mode',
    joined_as_agent: 'Connected with the user',
    disconnected_session: 'Disconnected the demo',
    blacklisted_ip: 'Blacklisted IP',
    unblacklisted_ip: 'Removed IP from blacklist',
    exported: 'Exported sessions',
    muted_participant: 'Muted a participant',
    unmuted_participant: 'Unmuted a participant',
    removed_participant: 'Removed a participant',
    join_request_accepted: 'Accepted a join request',
    join_request_rejected: 'Rejected a join request',
    join_request_blocked: 'Blocked a join request',
  };

  /** Actions tab: admin audit rows + whitelisted participant events, one sorted trail.
   *  Actor comes from the event payload (`actor`/`actorRole`, stamped by the demo page);
   *  events without one predate the stamp and were host-emitted by construction. */
  private buildActionRows(r: SessionReport): ActionRow[] {
    const hostName = r.userDetails?.name || r.session.hostName || 'Host';
    const rows: ActionRow[] = [];
    for (const a of r.audit || []) {
      const label = SessionReportComponent.AUDIT_ACTION_LABELS[a.action];
      if (!label) continue; // not a person's action — see AUDIT_ACTION_LABELS
      rows.push({
        label,
        name: a.adminName || 'Admin',
        role: SessionReportComponent.OBSERVER_AUDIT_ACTIONS.has(a.action) ? 'observer' : 'admin',
        detail: [a.reason, this.auditTarget(a)].filter(Boolean).join(' · ') || undefined,
        ip: a.adminIp || undefined,
        at: a.createdAt,
      });
    }
    for (const e of r.events || []) {
      if (!SessionReportComponent.ACTION_EVENT_TYPES.has(e.type)) continue;
      const p = (e.payload || {}) as Record<string, unknown>;
      // actorRole is stamped by the demo page: 'admin' = a joined Prism consultant
      // (never the AI). Events without one predate the stamp → host by construction.
      const role = p['actorRole'] === 'guest' ? 'guest'
        : p['actorRole'] === 'admin' ? 'admin' : 'host';
      rows.push({
        label: this.actionEventLabel(e.type, p),
        name: typeof p['actor'] === 'string' && p['actor'] ? p['actor'] : hostName,
        role,
        detail: this.actionEventDetail(e.type, p),
        at: e.at,
      });
    }
    return rows.sort((x, y) =>
      (parseApiDate(x.at)?.getTime() ?? 0) - (parseApiDate(y.at)?.getTime() ?? 0));
  }

  private actionEventLabel(type: string, p: Record<string, unknown>): string {
    switch (type) {
      case 'call.start': return 'Started demo — AI agent connected';
      case 'call.hold': return 'Put demo on hold';
      case 'call.resume': return 'Resumed demo';
      case 'call.end': return 'Ended demo';
      case 'action.copy_link': return p['link'] === 'invite' ? 'Copied invite link'
        : p['link'] === 'dialin' ? 'Copied phone dial-in details'
          : p['link'] === 'dialin-invite' ? 'Shared a phone invite (number + PIN)'
            : 'Copied share link';
      case 'action.connect_agent': return 'Requested a consultant';
      case 'action.signup': return p['via'] === 'form' ? 'Submitted signup form' : 'Opened signup page';
      case 'action.mic': return p['on'] ? 'Mic on' : 'Mic off';
      case 'action.camera': return p['on'] ? 'Camera on' : 'Camera off';
      case 'action.ai_text': return 'Sent message to AI agent';
      case 'action.phone_dialin': return 'Dialed in by phone — PIN accepted';
      case 'action.phone_joined': return 'Joined the demo by phone';
      case 'action.phone_left': return 'Left the phone call';
      case 'action.phone_bridge_failed': return 'Phone call could not reach the demo room';
      default: return this.eventLabel(type);
    }
  }

  /** Secondary line under an action ('' → hidden). Only the non-obvious bits. */
  private actionEventDetail(type: string, p: Record<string, unknown>): string | undefined {
    if (type === 'call.end' && typeof p['reason'] === 'string' && p['reason'] && p['reason'] !== 'manual') {
      return `reason: ${(p['reason'] as string).replace(/_/g, ' ')}`;
    }
    if (type === 'action.phone_bridge_failed') {
      // The usual cause: the caller's own demo page suspended behind the
      // dialer left the room empty and the gateway won't answer an empty room.
      // This is the one phone row that can have no participant behind it, so
      // the number stays on the detail line unless it already titles the row.
      const why = `dial status: ${String(p['dialStatus'] || 'unknown')} (attempt ${String(p['attempt'] || 1)})`;
      return [this.callerHint(p), why].filter(Boolean).join(' · ');
    }
    if (type.startsWith('action.phone')) return this.callerHint(p);
    return undefined;
  }

  /** "from +15205550142" / "from ***19" for a phone row, or '' when the row is
   *  already titled by its caller.
   *
   *  The report resolves the stored masked+hashed number to the real one and
   *  puts it in the actor slot (tracking_dashboard._name_phone_events), so
   *  repeating it here would say the same thing twice. What survives is the
   *  masked hint of a caller nobody could name: a session recorded before the
   *  roster carried numbers, or a call that never got past the PIN gate. */
  private callerHint(p: Record<string, unknown>): string {
    const phone = typeof p['phone'] === 'string' ? p['phone'] : '';
    if (!phone || p['actor'] === phone) return '';
    return `from ${phone.split('#')[0]}`;
  }

  /** Role chip tint — Host matches the participant badge, Admin keeps the audit purple. */
  actionRoleBadge(role: ActionRow['role']): string {
    return role === 'host' ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-300'
      : role === 'guest' ? 'bg-sky-50 text-sky-700 dark:bg-sky-400/10 dark:text-sky-300'
        : role === 'observer' ? 'bg-amber-50 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300'
          : 'bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-300';
  }

  /** Chip counts for the Actions role filter. */
  roleCount(role: ActionRow['role']): number {
    return this.actionRows().reduce((n, a) => n + (a.role === role ? 1 : 0), 0);
  }

  topPains(): string[] {
    const r = this.report();
    return (r?.research?.brief?.likelyPains || r?.outcome?.painsIdentified || []).slice(0, 6);
  }

  auditTarget(a: ReportAuditEntry): string | null {
    const pid = a.meta?.participantId;
    return pid ? `participant: ${pid}` : null;
  }

  /** Copy-chip feedback — flips the header chip to "Copied ✓" for a moment. */
  readonly copied = signal(false);
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  copySessionId(): void {
    const sid = this.session()?.sessionId;
    if (!sid || !navigator.clipboard) return;
    navigator.clipboard.writeText(sid).then(() => {
      this.copied.set(true);
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => this.copied.set(false), 1500);
    }).catch(() => { });
  }
}
